import { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import { streamProcess, getProcessById, setOnReceiveDatasCallback, setOnDoneCallback, setOnCrashCalllback } from '../../../services/streamingProcessApiService';
import { Process } from '../../../models/process';

/**
 * Hook utilisé pour mettre a jours l'affichage du composant en fonction du stream
 * @returns
 */
function useStreamer(processId: number) {
	const process = getProcessById(processId);
	const isProcessing = process.isProcessing;
	const logs = process.logs;

	const [processStream, setProcessStream] = useState({
		isProcessing,
		logs,
		error: '',
	});

	useEffect(() => {
		setOnReceiveDatasCallback(processId, (data) => {
			setProcessStream({ isProcessing: isProcessing, logs: [...logs], error: '' });
		});
		setOnDoneCallback(processId, () => {
			setProcessStream({ isProcessing: false, logs: [...logs], error: '' });
		});
		setOnCrashCalllback(processId, (error) => {
			setProcessStream({ isProcessing: false, logs: [...logs], error: error.message });
		});
		return () => {
			setOnReceiveDatasCallback(processId, null);
			setOnDoneCallback(processId, null);
			setOnCrashCalllback(processId, null);
		};
	}, [isProcessing, logs, processId]);
	return processStream;
}

export default function TabLine(props: { row: Process; env: String }) {
	const { row, env } = props;
	const processId = row.id;
	const [open, setOpen] = useState(false);
	const processStream = useStreamer(processId);
	// managment de la popup de lancement du process
	const handleClose = () => setOpen(false);
	const handleOpen = () => setOpen(true);
	// managment du lancement du process
	const startProcess = () => {
		handleClose();
		streamProcess(processId);
	};
	let typoColor = 'primary.main';
	if (processStream.error) {
		typoColor = 'error.main';
	} else if (processStream.isProcessing) {
		typoColor = 'warning.main';
	} else if (processStream.logs.length) {
		typoColor = 'success.main';
	}
	return (
		<Grid container spacing={0} sx={{ minHeight: 90, borderBottom: 1, alignItems: "center" }}>
			<Grid size={{ xs: 12, sm: 12, md: 9 }}>
				<Typography component="p" variant="h6" color={typoColor}>
					{row.name}
				</Typography>
			</Grid>
			<Grid size={{ xs: 12, sm: 12, md: 3 }}>
				<Stack spacing={2} direction="row">
					{!processStream.isProcessing && (
						<Button color={'primary'} variant="contained" endIcon={<PlayArrowIcon />} onClick={handleOpen}>
							Play
						</Button>
					)}
				</Stack>
				<Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
					<DialogTitle id="alert-dialog-title">{'Êtes-vous sûr de relancer le service ?'}</DialogTitle>
					<DialogContent>
						<DialogContentText id="alert-dialog-description">Environnement impacté : {env}</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose}>Retour</Button>
						<Button onClick={startProcess} autoFocus>
							Confirmer
						</Button>
					</DialogActions>
				</Dialog>
			</Grid>
			{processStream.logs?.map((line, index: number) => (
				<Grid size={{ xs: 12, sm: 12 }} key={index}>
					{line}
				</Grid>
			))}
			{processStream.error && <Typography color={'error'}>Error: {processStream.error}</Typography>}

			{processStream.isProcessing && (
				<Stack sx={{ color: 'grey.500' }} spacing={2} direction="row">
					<CircularProgress color="primary" />
				</Stack>
			)}
		</Grid>
	);
}
